import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const StatisticsSection = () => {
  const [animatedStats, setAnimatedStats] = useState({
    carsPurchased: 0,
    avgOfferTime: 0,
    customerSatisfaction: 0,
    totalPayout: 0
  });

  const finalStats = {
    carsPurchased: 50847,
    avgOfferTime: 18,
    customerSatisfaction: 98,
    totalPayout: 2.3
  };

  useEffect(() => {
    const animationDuration = 2000; // 2 seconds
    const steps = 60; // Number of animation steps
    const interval = animationDuration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setAnimatedStats({
        carsPurchased: Math.floor(finalStats.carsPurchased * progress),
        avgOfferTime: Math.floor(finalStats.avgOfferTime * progress),
        customerSatisfaction: Math.floor(finalStats.customerSatisfaction * progress),
        totalPayout: (finalStats.totalPayout * progress).toFixed(1)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedStats(finalStats);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      icon: 'Car',
      label: 'Cars Purchased',
      value: animatedStats.carsPurchased.toLocaleString(),
      suffix: '+',
      color: 'blue',
      description: 'Vehicles bought nationwide'
    },
    {
      icon: 'Clock',
      label: 'Avg. Offer Time',
      value: animatedStats.avgOfferTime,
      suffix: ' hours',
      color: 'green',
      description: 'From upload to offer'
    },
    {
      icon: 'Heart',
      label: 'Customer Satisfaction',
      value: animatedStats.customerSatisfaction,
      suffix: '%',
      color: 'purple',
      description: 'Happy customers rate us 5★'
    },
    {
      icon: 'DollarSign',
      label: 'Total Payout',
      value: '$' + animatedStats.totalPayout,
      suffix: 'B',
      color: 'orange',
      description: 'Paid to our sellers'
    }
  ];

  const colorMap = {
    blue: {
      bg: 'from-blue-500 to-blue-600',
      text: 'text-blue-600',
      light: 'from-blue-50 to-blue-100'
    },
    green: {
      bg: 'from-green-500 to-green-600',
      text: 'text-green-600',
      light: 'from-green-50 to-green-100'
    },
    purple: {
      bg: 'from-purple-500 to-purple-600',
      text: 'text-purple-600',
      light: 'from-purple-50 to-purple-100'
    },
    orange: {
      bg: 'from-orange-500 to-orange-600',
      text: 'text-orange-600',
      light: 'from-orange-50 to-orange-100'
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Trusted by Thousands of Sellers
          </h2>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto">
            Our numbers speak for themselves. Join the growing community of satisfied customers 
            who've made iSell.car their go-to platform for selling vehicles.
          </p>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300 group border border-white/20"
            >
              {/* Icon */}
              <div className="mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${colorMap[stat.color].bg} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={stat.icon} size={32} color="white" />
                </div>
              </div>

              {/* Value */}
              <div className="mb-2">
                <span className="text-3xl lg:text-4xl font-bold">
                  {stat.value}
                </span>
                <span className="text-xl text-blue-200">
                  {stat.suffix}
                </span>
              </div>

              {/* Label */}
              <h3 className="text-lg font-semibold mb-2">
                {stat.label}
              </h3>

              {/* Description */}
              <p className="text-sm text-blue-200">
                {stat.description}
              </p>

              {/* Decorative element */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-white/30 rounded-full animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Bottom Achievement Section */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-8 bg-white/10 backdrop-blur-md rounded-2xl px-8 py-6 border border-white/20">
            <div className="flex items-center space-x-3">
              <Icon name="Award" size={24} className="text-yellow-400" />
              <div className="text-left">
                <div className="text-lg font-bold">Industry Leader</div>
                <div className="text-sm text-blue-200">#1 Car Buying Platform</div>
              </div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="flex items-center space-x-3">
              <Icon name="Shield" size={24} className="text-green-400" />
              <div className="text-left">
                <div className="text-lg font-bold">Verified Secure</div>
                <div className="text-sm text-blue-200">SSL Encrypted & Insured</div>
              </div>
            </div>
            
            <div className="w-px h-12 bg-white/20"></div>
            
            <div className="flex items-center space-x-3">
              <Icon name="Zap" size={24} className="text-blue-400" />
              <div className="text-left">
                <div className="text-lg font-bold">Lightning Fast</div>
                <div className="text-sm text-blue-200">Average 18hr turnaround</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;